import { useCallback } from 'react';
import { debounce } from 'lodash';
import { message } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useUpdateResolution886ReviewReport } from '@/hooks/v2/resolution-886';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import { Resolution886ResultEnum } from '@/enums/Resolution886';

const BOOLEAN_FLAG_TRUE = 1;

interface Worker {
	id: string;
	name: string;
}

interface File {
	id: string;
	original_name: string;
	generated_name: string;
}

interface UpdateCommentParams {
	comment: string;
	result_type: Resolution886ResultEnum;
}

export const useUpdateComment = () => {
	const { mutateAsync: updateComment, isLoading: isUpdatingComment } = useUpdateResolution886ReviewReport();
	const { report, lastReviewId } = useResolution886Report();
	const { organization, company } = useApplicationContext();

	const buildUpdateParams = useCallback(
		(comment: string, result_type: Resolution886ResultEnum) => {
			const currentReview = report?.reviews?.[report.reviews.length - 1];

			if (!currentReview || !report || !lastReviewId || !organization?.id || !company?.id) {
				throw new Error('Missing required data for comment update');
			}

			return {
				id: report.id,
				review_id: lastReviewId,
				name: currentReview.name || '',
				workstation_id: report.workstation?.id || '',
				sector_id: report.sector?.id || '',
				line_id: report.line?.id || '',
				company_id: company.id,
				organization_id: organization.id,
				employer_id: currentReview.employer?.id || '',
				evaluator_id: currentReview.evaluator?.id || '',
				occupational_doctor_id: currentReview.occupational_doctor?.id || '',
				safety_and_hygiene_responsible_id: currentReview.safety_and_hygiene_responsible?.id || '',
				workers_number: currentReview.workers_number || 0,
				symptom_location: currentReview.symptom_location || '',
				early_manifestation: currentReview.early_manifestation === BOOLEAN_FLAG_TRUE,
				written_work_procedure: currentReview.written_work_procedure === BOOLEAN_FLAG_TRUE,
				training: currentReview.training === BOOLEAN_FLAG_TRUE,
				worker_list_id: currentReview.workers?.map((worker: Worker) => worker.id) || [],
				file_list_id: currentReview.files?.map((file: File) => file.id) || [],
				review_comment: result_type === Resolution886ResultEnum.INITIAL ? comment : undefined,
				comment: result_type === Resolution886ResultEnum.GENERAL ? comment : undefined
			};
		},
		[report, lastReviewId, organization?.id, company?.id]
	);

	const debouncedUpdateComment = useCallback(
		debounce(async ({ comment, result_type }: UpdateCommentParams) => {
			try {
				const updateParams = buildUpdateParams(comment, result_type);
				await updateComment(updateParams);
			} catch (error) {
				message.error(I18n.get('Error updating comment. Please try again.'));
			}
		}, 1000),
		[buildUpdateParams, updateComment]
	);

	return {
		updateComment: debouncedUpdateComment,
		isUpdatingComment
	};
};
